import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Clock, Link as LinkIcon, FlaskConical, Plus, Play, CheckCircle,
  XCircle, Send, Download, FileText, FileCode, FileDown, Edit2,
  Thermometer, Wrench, Loader2, Calculator, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import { useAuth } from '@/contexts/AuthContext';
import StatusBadge from '@/components/StatusBadge';
import RefLink from '@/components/RefLink';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';

// Route /calibrations/:id, read-only detail.
// Tabs: Přehled / Historie / Dokumenty / Související.

const TABS_BASE = [
  { id: 'overview', labelKey: 'calibrations.detail-tab-overview', icon: FlaskConical },
  { id: 'history', labelKey: 'calibrations.detail-tab-history', icon: Clock },
  { id: 'documents', labelKey: 'calibrations.detail-tab-documents', icon: FileText },
  { id: 'related', labelKey: 'calibrations.detail-tab-related', icon: LinkIcon },
];

// Lab labely se překládají přes t('calibrations.lab-{id}') v komponentě.

function TabButton({ tab, active, onClick }) {
  const { t } = useTranslation();
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors"
      style={{
        color: active ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
        borderBottom: active ? '2px solid var(--ct-accent)' : '2px solid transparent',
      }}
    >
      <Icon className="w-4 h-4" />
      {t(tab.labelKey)}
    </button>
  );
}

function TimelineEvent({ icon: Icon, tone, at, title, description, isLast = false }) {
  const { t } = useTranslation();
  const toneColors = {
    info: 'var(--ct-info)',
    success: 'var(--ct-success)',
    warning: 'var(--ct-warning)',
    danger: 'var(--ct-error)',
  };
  const color = toneColors[tone] || 'var(--ct-text-secondary)';
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--ct-bg-secondary)', border: `2px solid ${color}` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        {!isLast && <div className="flex-1 w-px mt-1" style={{ background: 'var(--ct-border)' }} />}
      </div>
      <div className={`flex-1 -mt-0.5 ${isLast ? '' : 'pb-5'}`}>
        <div className="text-sm font-medium" style={{ color: 'var(--ct-text)' }}>{title}</div>
        {description && (
          <div className="text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>{description}</div>
        )}
        {at && (
          <div className="text-xs font-mono mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
            {new Date(at).toLocaleString(t('common.date-locale'))}
          </div>
        )}
      </div>
    </div>
  );
}

// Priorita + přiřazený technik
function QueueSection({ cal, onRefresh }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/users').then(r => setUsers((r.data || []).filter(u =>
      u.isAdmin || (u.permissions || []).some(p => p.module === cal.laboratory && (p.actions || []).includes('calibrate'))
    ))).catch(() => {});
  }, [cal.laboratory]);

  const setPriority = async (priority) => {
    setBusy(true);
    try {
      await api.put(`/calibrations/${cal.id}/priority`, { priority });
      toast.success(t('calibrations.queue-toast-priority', { priority: t(`calibrations.priority-${priority}`) }));
      onRefresh?.();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };
  const setAssigned = async (user_id) => {
    setBusy(true);
    try {
      await api.put(`/calibrations/${cal.id}/assign`, { user_id });
      toast.success(user_id ? t('calibrations.queue-toast-assigned') : t('calibrations.queue-toast-unassigned'));
      onRefresh?.();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };

  const cur = cal.priority || 'normal';
  return (
    <section>
      <h3 className="ct-eyebrow mb-2">{t('calibrations.queue-title')}</h3>
      <div className="ct-panel p-4 grid grid-cols-2 gap-4">
        <div>
          <div className="ct-eyebrow mb-1">{t('calibrations.queue-priority-label')}</div>
          <div className="flex gap-1">
            {['low', 'normal', 'high'].map(p => (
              <button
                key={p}
                onClick={() => setPriority(p)}
                disabled={busy}
                className="px-2 py-1 text-xs rounded transition-colors"
                style={{
                  background: cur === p ? 'var(--ct-accent)' : 'var(--ct-bg-secondary)',
                  color: cur === p ? '#fff' : 'var(--ct-text-secondary)',
                  border: '1px solid var(--ct-border)',
                }}
              >
                {t(`calibrations.priority-${p}`)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="ct-eyebrow mb-1">{t('calibrations.queue-assigned-label')}</div>
          <select
            value={cal.assignedTo || ''}
            onChange={e => setAssigned(e.target.value || null)}
            disabled={busy}
            className="ct-input text-sm w-full"
          >
            <option value="">{t('calibrations.queue-unassigned')}</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>
    </section>
  );
}

// Aklimatizační log
function AcclimatizationSection({ cal, onRefresh }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      await api.put(`/calibrations/${cal.id}/acclimatization-start`, {
        environmentalConditions: cal.environmentalConditions || {},
      });
      toast.success(t('calibrations.acclim-toast-started'));
      onRefresh?.();
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };
  const startedAt = cal.acclimatizationStartedAt;
  const elapsedMin = startedAt ? Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000) : 0;
  const isReady = elapsedMin >= 120;  // 2h = 120 min
  return (
    <section>
      <h3 className="ct-eyebrow mb-2 flex items-center gap-1">
        <Thermometer className="w-3 h-3" /> {t('calibrations.acclim-title')}
      </h3>
      <div className="ct-panel p-4">
        {startedAt ? (
          <div className="space-y-1 text-sm">
            <div>
              <span className="ct-eyebrow mr-2">{t('calibrations.acclim-started-label')}</span>
              <span className="font-mono">{new Date(startedAt).toLocaleString(t('common.date-locale'))}</span>
              {cal.acclimatizationStartedByName && (
                <span className="text-xs ml-2" style={{ color: 'var(--ct-text-secondary)' }}>
                  ({cal.acclimatizationStartedByName})
                </span>
              )}
            </div>
            <div>
              <span className="ct-eyebrow mr-2">{t('calibrations.acclim-elapsed-label')}</span>
              <span className="font-mono" style={{ color: isReady ? 'var(--ct-success)' : 'var(--ct-warning)' }}>
                {t('calibrations.acclim-elapsed-format', { h: Math.floor(elapsedMin / 60), min: elapsedMin % 60 })}
              </span>
              <span className="text-xs ml-2" style={{ color: 'var(--ct-text-secondary)' }}>
                {isReady ? t('calibrations.acclim-ready') : t('calibrations.acclim-remaining', { min: Math.max(0, 120 - elapsedMin) })}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('calibrations.acclim-help')}
            </p>
            <Button onClick={start} disabled={busy} className="ct-btn-primary gap-1 whitespace-nowrap">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Thermometer className="w-4 h-4" />}
              {t('calibrations.acclim-btn-start')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

// Záznam o justáži
function AdjustmentSection({ cal, onRefresh }) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [performed, setPerformed] = useState(true);
  const [description, setDescription] = useState('');
  const [errorBefore, setErrorBefore] = useState('');
  const [errorAfter, setErrorAfter] = useState('');
  const [busy, setBusy] = useState(false);
  const adj = cal.adjustment;
  // Aktuální max. absolutní chyba z měření — předvyplní „před justáží".
  const maxAbsError = (cal.measurements?.points || [])
    .reduce((m, p) => Math.max(m, Math.abs(Number(p.error) || 0)), 0);
  const openForm = (fromAdj) => {
    if (fromAdj) {
      setPerformed(fromAdj.performed ?? true);
      setDescription(fromAdj.description || '');
      setErrorBefore(fromAdj.errorBefore != null ? String(fromAdj.errorBefore) : '');
      setErrorAfter(fromAdj.errorAfter != null ? String(fromAdj.errorAfter) : '');
    } else {
      setPerformed(true);
      setDescription('');
      setErrorBefore(maxAbsError ? String(maxAbsError) : '');
      setErrorAfter('');
    }
    setShowForm(true);
  };
  const save = async () => {
    if (performed && !description.trim()) {
      toast.error(t('calibrations.adj-error-no-desc'));
      return;
    }
    setBusy(true);
    try {
      await api.put(`/calibrations/${cal.id}/adjustment`, {
        performed,
        description: description.trim(),
        errorBefore: errorBefore === '' ? null : parseFloat(errorBefore),
        errorAfter: errorAfter === '' ? null : parseFloat(errorAfter),
      });
      toast.success(t('calibrations.adj-toast-saved'));
      setShowForm(false);
      setDescription('');
      onRefresh?.();
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h3 className="ct-eyebrow mb-2 flex items-center gap-1">
        <Wrench className="w-3 h-3" /> {t('calibrations.adj-title')}
      </h3>
      <div className="ct-panel p-4">
        {adj ? (
          <div className="space-y-1 text-sm">
            <div>
              <span className="ct-eyebrow mr-2">{t('calibrations.adj-performed-label')}</span>
              <span style={{ color: adj.performed ? 'var(--ct-warning)' : 'var(--ct-text-secondary)' }}>
                {adj.performed ? t('calibrations.adj-performed-yes') : t('calibrations.adj-performed-no')}
              </span>
              {adj.doneAt && (
                <span className="text-xs ml-2" style={{ color: 'var(--ct-text-secondary)' }}>
                  · {new Date(adj.doneAt).toLocaleString(t('common.date-locale'))} ({adj.doneByName || '—'})
                </span>
              )}
            </div>
            {adj.description && (
              <div>
                <span className="ct-eyebrow mr-2">{t('calibrations.adj-desc-label')}</span>
                <span>{adj.description}</span>
              </div>
            )}
            {(adj.errorBefore != null || adj.errorAfter != null) && (
              <div>
                <span className="ct-eyebrow mr-2">Max. chyba (před → po):</span>
                <span className="font-mono">
                  {adj.errorBefore != null ? adj.errorBefore : '—'} → {adj.errorAfter != null ? adj.errorAfter : '—'}
                </span>
              </div>
            )}
            <button
              onClick={() => openForm(adj)}
              className="text-xs underline mt-2"
              style={{ color: 'var(--ct-accent)' }}
            >
              {t('calibrations.adj-btn-edit')}
            </button>
          </div>
        ) : !showForm ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('calibrations.adj-help')}
            </p>
            <Button onClick={() => openForm(null)} className="ct-btn-secondary gap-1 whitespace-nowrap">
              <Wrench className="w-4 h-4" /> {t('calibrations.adj-btn-record')}
            </Button>
          </div>
        ) : null}

        {showForm && (
          <div className="space-y-3 mt-3">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-sm cursor-pointer">
                <input
                  type="radio"
                  checked={performed === true}
                  onChange={() => setPerformed(true)}
                />
                {t('calibrations.adj-radio-performed')}
              </label>
              <label className="flex items-center gap-1 text-sm cursor-pointer">
                <input
                  type="radio"
                  checked={performed === false}
                  onChange={() => setPerformed(false)}
                />
                {t('calibrations.adj-radio-not-performed')}
              </label>
            </div>
            {performed && (
              <>
                <Textarea
                  placeholder={t('calibrations.adj-textarea-placeholder')}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  className="text-sm"
                />
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <label className="flex items-center gap-1" style={{ color: 'var(--ct-text-secondary)' }}>
                    Max. chyba před justáží:
                    <input type="number" value={errorBefore} onChange={e => setErrorBefore(e.target.value)}
                      className="h-8 w-24 text-sm rounded px-2"
                      style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }} />
                  </label>
                  <label className="flex items-center gap-1" style={{ color: 'var(--ct-text-secondary)' }}>
                    po justáži:
                    <input type="number" value={errorAfter} onChange={e => setErrorAfter(e.target.value)}
                      className="h-8 w-24 text-sm rounded px-2"
                      style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }} />
                  </label>
                </div>
                <span className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                  „Před" je předvyplněno z max. naměřené chyby. „Po" doplňte po přeměření.
                </span>
              </>
            )}
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={busy} className="ct-btn-primary gap-1">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {t('calibrations.adj-btn-save')}
              </Button>
              <Button onClick={() => setShowForm(false)} variant="outline" className="text-xs">
                {t('calibrations.adj-btn-cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// GUM auto-výpočet nejistot
function UncertaintySection({ cal, onRefresh }) {
  const { t } = useTranslation();
  const [showCalc, setShowCalc] = useState(false);
  const [etalonU, setEtalonU] = useState('');
  const [etalonK, setEtalonK] = useState('2');
  const [resolution, setResolution] = useState('');
  const [k, setK] = useState('2');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const unc = cal.uncertaintyResults;
  const hasMeasurements = (cal.measurements?.points || []).length > 0;

  const calc = async (persist = false) => {
    setBusy(true);
    try {
      const additionalComponents = [];
      if (resolution && parseFloat(resolution) > 0) {
        additionalComponents.push({
          name: t('calibrations.unc-resolution-component-name'),
          value: parseFloat(resolution),
          distribution: 'rectangular',
        });
      }
      const r = await api.post(`/calibrations/${cal.id}/calculate-uncertainty`, {
        etalonUncertaintyExpanded: etalonU ? parseFloat(etalonU) : null,
        etalonCoverageFactor: parseFloat(etalonK) || 2,
        additionalComponents,
        coverageFactor: parseFloat(k) || 2,
        persist,
      });
      setPreview(r.data);
      if (persist) {
        toast.success(t('calibrations.unc-toast-saved'));
        setShowCalc(false);
        onRefresh?.();
      } else {
        toast.success(t('calibrations.unc-toast-preview'));
      }
    } catch (e) {
      toastApiError(e, t('calibrations.unc-error-calc'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h3 className="ct-eyebrow mb-2 flex items-center gap-1">
        <Calculator className="w-3 h-3" /> {t('calibrations.unc-title')}
      </h3>
      <div className="ct-panel p-4">
        {unc && unc.expanded ? (
          <div className="space-y-1 text-sm">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <div className="ct-eyebrow">u<sub>A</sub> {t('calibrations.unc-uA-label')}</div>
                <div className="font-mono">{unc.typeA ?? '—'}</div>
              </div>
              <div>
                <div className="ct-eyebrow">u<sub>B</sub> {t('calibrations.unc-uB-label')}</div>
                <div className="font-mono">{unc.typeB ?? '—'}</div>
              </div>
              <div>
                <div className="ct-eyebrow">u<sub>c</sub> {t('calibrations.unc-uc-label')}</div>
                <div className="font-mono">{unc.combined ?? '—'}</div>
              </div>
              <div>
                <div className="ct-eyebrow">{t('calibrations.unc-U-label', { k: unc.coverageFactor || 2 })}</div>
                <div className="font-mono font-bold" style={{ color: 'var(--ct-accent)' }}>
                  {unc.expanded ?? '—'}
                </div>
              </div>
            </div>
            {unc.calculatedAt && (
              <div className="text-xs mt-2" style={{ color: 'var(--ct-text-secondary)' }}>
                {t('calibrations.unc-calculated-at', {
                  date: new Date(unc.calculatedAt).toLocaleString(t('common.date-locale')),
                  by: unc.calculatedByName ? ` · ${unc.calculatedByName}` : '',
                })}
              </div>
            )}
            <button
              onClick={() => setShowCalc(true)}
              className="text-xs underline mt-2"
              style={{ color: 'var(--ct-accent)' }}
            >
              {t('calibrations.unc-btn-recalc')}
            </button>
          </div>
        ) : !showCalc ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
              {hasMeasurements
                ? t('calibrations.unc-help-has-measurements')
                : t('calibrations.unc-help-no-measurements')}
            </p>
            <Button
              onClick={() => setShowCalc(true)}
              className="ct-btn-secondary gap-1 whitespace-nowrap"
              disabled={!hasMeasurements}
            >
              <Calculator className="w-4 h-4" /> {t('calibrations.unc-btn-calc')}
            </Button>
          </div>
        ) : null}

        {showCalc && (
          <div className="space-y-3 mt-3 pt-3" style={{ borderTop: '1px solid var(--ct-border)' }}>
            <div className="grid grid-cols-2 gap-3">
              <div className="ct-form-field">
                <label className="ct-form-label">{t('calibrations.unc-label-etalonU')}</label>
                <input
                  type="number" step="0.0001"
                  value={etalonU}
                  onChange={e => setEtalonU(e.target.value)}
                  placeholder={t('calibrations.unc-label-etalonU-placeholder')}
                  className="ct-input text-sm"
                />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">{t('calibrations.unc-label-etalonK')}</label>
                <input
                  type="number" step="0.1" value={etalonK}
                  onChange={e => setEtalonK(e.target.value)}
                  className="ct-input text-sm"
                />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">{t('calibrations.unc-label-resolution')}</label>
                <input
                  type="number" step="0.0001"
                  value={resolution}
                  onChange={e => setResolution(e.target.value)}
                  placeholder={t('calibrations.unc-label-resolution-placeholder')}
                  className="ct-input text-sm"
                />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">{t('calibrations.unc-label-k')}</label>
                <input
                  type="number" step="0.1" value={k}
                  onChange={e => setK(e.target.value)}
                  className="ct-input text-sm"
                />
              </div>
            </div>

            {preview && (
              <div className="ct-panel p-3" style={{ background: 'var(--ct-bg-secondary)' }}>
                <div className="text-xs ct-eyebrow mb-2">{t('calibrations.unc-preview-title')}</div>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div>u<sub>A</sub>: <span className="font-mono">{preview.typeA}</span></div>
                  <div>u<sub>B</sub>: <span className="font-mono">{preview.typeB}</span></div>
                  <div>u<sub>c</sub>: <span className="font-mono">{preview.combined}</span></div>
                  <div>U (k={preview.coverageFactor}): <span className="font-mono font-bold"
                    style={{ color: 'var(--ct-accent)' }}>{preview.expanded}</span></div>
                </div>
                {preview.breakdown?.typeA?.perPoint && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer" style={{ color: 'var(--ct-text-secondary)' }}>
                      {t('calibrations.unc-preview-details')}
                    </summary>
                    <div className="mt-1 font-mono text-[11px] whitespace-pre-wrap">
                      {JSON.stringify(preview.breakdown, null, 2)}
                    </div>
                  </details>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={() => calc(false)} className="ct-btn-secondary gap-1" disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
                {t('calibrations.unc-btn-preview')}
              </Button>
              <Button
                onClick={() => calc(true)}
                className="ct-btn-primary gap-1"
                disabled={busy || !preview}
                title={!preview ? t('calibrations.unc-btn-save-tooltip-need-preview') : t('calibrations.unc-btn-save-tooltip-ok')}
              >
                <CheckCircle className="w-4 h-4" /> {t('calibrations.unc-btn-save')}
              </Button>
              <Button onClick={() => { setShowCalc(false); setPreview(null); }} className="ct-btn-ghost" disabled={busy}>
                {t('calibrations.unc-btn-cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function OverviewTab({ cal, methodology, etalon, onRefresh }) {
  const { t } = useTranslation();
  const points = cal.measurements?.points || [];
  return (
    <div className="space-y-6">
      {/* Rejection panel if applicable */}
      {cal.status === 'rejected' && cal.rejectionReason && (
        <section>
          <div
            className="ct-panel p-4"
            style={{ borderColor: 'var(--ct-error)', background: 'var(--ct-error-soft)' }}
          >
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--ct-error)' }} />
              <div>
                <div className="font-medium" style={{ color: 'var(--ct-error)' }}>{t('calibrations.overview-rejection-title')}</div>
                <div className="text-sm mt-1" style={{ color: 'var(--ct-text)', whiteSpace: 'pre-wrap' }}>
                  {cal.rejectionReason}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Instrument */}
      <section>
        <h3 className="ct-eyebrow mb-2">{t('calibrations.overview-instrument-section')}</h3>
        <div className="ct-panel p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-instrument-name')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{cal.instrumentName || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-instrument-type')}</div>
              <div className="font-mono text-xs" style={{ color: 'var(--ct-text)' }}>{cal.instrumentType || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-instrument-manufacturer')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{cal.instrumentManufacturer || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-instrument-marking')}</div>
              <div className="font-mono text-xs" style={{ color: 'var(--ct-text)' }}>
                {[cal.instrumentMarking1, cal.instrumentMarking2].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sonda — měřicí kanál na přístroji s metodikou a rozsahem */}
      {cal.probeId && (
        <section>
          <h3 className="ct-eyebrow mb-2">{t('calibrations.overview-probe-section')}</h3>
          <div className="ct-panel p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="ct-eyebrow">{t('calibrations.overview-probe-code')}</div>
                <div className="font-mono text-xs" style={{ color: 'var(--ct-accent)' }}>{cal.probeCode || '—'}</div>
              </div>
              <div>
                <div className="ct-eyebrow">{t('calibrations.overview-probe-name')}</div>
                <div style={{ color: 'var(--ct-text)' }}>{cal.probeName || '—'}</div>
              </div>
              <div>
                <div className="ct-eyebrow">{t('calibrations.overview-probe-range')}</div>
                <div className="font-mono text-xs" style={{ color: 'var(--ct-text)' }}>
                  {(cal.probeRangeMin !== undefined && cal.probeRangeMax !== undefined)
                    ? `${cal.probeRangeMin} – ${cal.probeRangeMax} ${cal.probeUnitSymbol || ''}`
                    : '—'}
                </div>
              </div>
              <div>
                <div className="ct-eyebrow">{t('calibrations.overview-probe-type')}</div>
                <div style={{ color: 'var(--ct-text)' }}>
                  {cal.probeTypeName || '—'}
                  {cal.probeRead && (
                    <span className="ml-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                      ({cal.probeRead === 'digital' ? t('calibrations.overview-probe-read-digital') : t('calibrations.overview-probe-read-analog')})
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Methodology + Etalon */}
      <section>
        <h3 className="ct-eyebrow mb-2">{t('calibrations.overview-method-section')}</h3>
        <div className="ct-panel p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-method-laboratory')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{cal.laboratory ? t(`calibrations.lab-${cal.laboratory}`) : '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-method-methodology')}</div>
              <div style={{ color: 'var(--ct-text)' }}>
                {methodology?.code
                  ? <>{methodology.code} — <span style={{ color: 'var(--ct-text-secondary)' }}>{methodology.title || ''}</span></>
                  : '—'}
              </div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-method-etalon')}</div>
              <div style={{ color: 'var(--ct-text)' }}>
                {etalon?.code
                  ? <>{etalon.code} — <span style={{ color: 'var(--ct-text-secondary)' }}>{etalon.name || ''}</span></>
                  : '—'}
              </div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('calibrations.overview-method-calibrator')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{cal.calibratingUserName || '—'}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Fronta práce */}
      {['pending_intake', 'in_progress'].includes(cal.status) && (
        <QueueSection cal={cal} onRefresh={onRefresh} />
      )}

      {/* Aklimatizace + Justáž */}
      {['pending_intake', 'in_progress', 'completed'].includes(cal.status) && (
        <>
          <AcclimatizationSection cal={cal} onRefresh={onRefresh} />
          <AdjustmentSection cal={cal} onRefresh={onRefresh} />
        </>
      )}

      {/* GUM auto-výpočet nejistot */}
      {['in_progress', 'completed'].includes(cal.status) && (
        <UncertaintySection cal={cal} onRefresh={onRefresh} />
      )}

      {/* Result */}
      {cal.result && (
        <section>
          <h3 className="ct-eyebrow mb-2">{t('calibrations.overview-result-section')}</h3>
          <div className="ct-panel p-4 flex items-center gap-3">
            <StatusBadge status={cal.result} size="md" />
            {cal.certificateNumber && (
              <div className="text-sm">
                <span className="ct-eyebrow mr-2">{t('calibrations.overview-result-cert-number')}</span>
                <span className="font-mono" style={{ color: 'var(--ct-accent)' }}>{cal.certificateNumber}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Measurements */}
      <section>
        <h3 className="ct-eyebrow mb-2">{t('calibrations.overview-measurements-section', { count: points.length })}</h3>
        {points.length === 0 ? (
          <div className="ct-panel p-4">
            <div className="text-sm italic" style={{ color: 'var(--ct-text-disabled)' }}>{t('calibrations.overview-measurements-empty')}</div>
          </div>
        ) : (
          <div className="ct-panel overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('calibrations.overview-measurements-col-num')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('calibrations.overview-measurements-col-nominal')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('calibrations.overview-measurements-col-series')}</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p, idx) => {
                  const series = p.series || [];
                  return (
                    <tr key={idx} style={{ borderTop: '1px solid var(--ct-border)' }}>
                      <td className="py-2 px-3 font-mono text-xs">{idx + 1}</td>
                      <td className="py-2 px-3 font-mono">{p.nominal ?? '—'}</td>
                      <td className="py-2 px-3 font-mono text-xs">
                        {series.map((s, i) => (
                          <span key={i} className="mr-3" style={{ color: 'var(--ct-text-secondary)' }}>
                            {s.ascending ?? '—'}↑ / {s.descending ?? '—'}↓
                          </span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {cal.notes && (
        <section>
          <h3 className="ct-eyebrow mb-2">{t('calibrations.overview-notes-section')}</h3>
          <div className="ct-panel p-4 text-sm" style={{ color: 'var(--ct-text)', whiteSpace: 'pre-wrap' }}>
            {cal.notes}
          </div>
        </section>
      )}
    </div>
  );
}

const AUDIT_ACTION_LABELS = {
  status_changed: 'Změna stavu',
  document_issued: 'Vydání dokumentu',
};
const AUDIT_STATUS_LABELS = {
  completed: 'dokončeno',
  under_technical_review: 'technická kontrola',
  approved: 'schváleno',
  rejected: 'zamítnuto',
  pending_approval: 'čeká na schválení',
};

function AuditTimeline({ calId }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/calibrations/${calId}/audit`)
      .then(r => { if (!cancelled) setRows(r.data || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [calId]);

  if (rows === null) return null;
  if (rows.length === 0) return null;

  const fmt = (iso) => { try { return new Date(iso).toLocaleString('cs-CZ'); } catch { return iso; } };

  return (
    <div className="ct-panel p-4" data-testid="audit-timeline">
      <div className="ct-panel-header flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4" /> Auditní záznam (kdo / kdy / co)
      </div>
      <div className="space-y-2 text-sm">
        {rows.map((e, idx) => (
          <div key={idx} className="flex items-start justify-between gap-3 border-b pb-2 last:border-0"
               style={{ borderColor: 'var(--ct-border)' }}>
            <div>
              <span className="font-medium">{AUDIT_ACTION_LABELS[e.action] || e.action}</span>
              {e.toStatus && (
                <span className="text-muted-foreground"> → {AUDIT_STATUS_LABELS[e.toStatus] || e.toStatus}</span>
              )}
              {e.detail?.certificateNumber && (
                <span className="text-muted-foreground"> · {e.detail.certificateNumber}</span>
              )}
              <div className="text-xs text-muted-foreground">{e.userName || '—'}</div>
            </div>
            <div className="text-xs text-muted-foreground whitespace-nowrap">{fmt(e.at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTab({ cal }) {
  const { t } = useTranslation();
  const resultLabel = cal.result === 'pass'
    ? t('calibrations.history-result-pass')
    : cal.result === 'fail'
      ? t('calibrations.history-result-fail')
      : '—';
  const events = [
    cal.createdAt && {
      icon: Plus,
      tone: 'info',
      at: cal.createdAt,
      title: t('calibrations.history-event-created'),
      description: t('calibrations.history-event-created-desc', {
        user: cal.calibratingUserName || t('calibrations.history-event-created-fallback-user'),
        intake: cal.intakeProtocolNumber || '—',
      }),
    },
    cal.completedAt && {
      icon: Play,
      tone: 'info',
      at: cal.completedAt,
      title: t('calibrations.history-event-measured'),
      description: t('calibrations.history-event-measured-desc', { result: resultLabel }),
    },
    cal.submittedForApprovalAt && {
      icon: Send,
      tone: 'warning',
      at: cal.submittedForApprovalAt,
      title: t('calibrations.history-event-submitted'),
      description: cal.submittedForApprovalByName
        ? t('calibrations.history-event-submitted-by', { name: cal.submittedForApprovalByName })
        : t('calibrations.history-event-submitted-default'),
    },
    cal.status === 'approved' && cal.approvedAt && {
      icon: CheckCircle,
      tone: 'success',
      at: cal.approvedAt,
      title: t('calibrations.history-event-approved'),
      description: t('calibrations.history-event-approved-desc', {
        cert: cal.certificateNumber || '—',
        warning: cal.dccXmlValid === false ? t('calibrations.history-event-approved-dcc-warning') : '',
      }),
    },
    cal.status === 'rejected' && {
      icon: XCircle,
      tone: 'danger',
      at: null,
      title: t('calibrations.history-event-rejected'),
      description: cal.rejectionReason || t('calibrations.history-event-rejected-default'),
    },
  ].filter(Boolean);

  if (events.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState icon={Clock} title={t('calibrations.history-empty-title')} description={t('calibrations.history-empty-desc')} />
        <AuditTimeline calId={cal.id} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="ct-panel p-4">
        {events.map((e, idx) => (
          <TimelineEvent key={idx} {...e} isLast={idx === events.length - 1} />
        ))}
      </div>
      <AuditTimeline calId={cal.id} />
    </div>
  );
}

function DocumentsTab({ cal, onDownloadPdf, onDownloadDcc }) {
  const { t } = useTranslation();
  const isApproved = cal.status === 'approved' && cal.certificateNumber;

  if (!isApproved) {
    return (
      <EmptyState
        icon={FileText}
        title={t('calibrations.documents-empty-title')}
        description={
          cal.status === 'rejected'
            ? t('calibrations.documents-empty-rejected')
            : t('calibrations.documents-empty-default')
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="ct-panel p-4 flex items-center gap-4">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--ct-info-soft)' }}
        >
          <FileDown className="w-6 h-6" style={{ color: 'var(--ct-info)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium" style={{ color: 'var(--ct-text)' }}>{t('calibrations.documents-pdf-title')}</div>
          <div className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
            {t('calibrations.documents-pdf-desc', { number: cal.certificateNumber })}
          </div>
        </div>
        <Button onClick={onDownloadPdf} className="ct-btn-secondary gap-1 text-xs">
          <Download className="w-3.5 h-3.5" /> {t('calibrations.documents-btn-download')}
        </Button>
      </div>

      <div className="ct-panel p-4 flex items-center gap-4">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--ct-accent-soft)' }}
        >
          <FileCode className="w-6 h-6" style={{ color: 'var(--ct-accent)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium flex items-center gap-2" style={{ color: 'var(--ct-text)' }}>
            {t('calibrations.documents-dcc-title')}
            {cal.dccXmlValid === false && (
              <StatusBadge tone="warning" size="sm">{t('calibrations.documents-dcc-warning')}</StatusBadge>
            )}
          </div>
          <div className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
            {t('calibrations.documents-dcc-desc')}
          </div>
        </div>
        <Button onClick={onDownloadDcc} className="ct-btn-secondary gap-1 text-xs">
          <Download className="w-3.5 h-3.5" /> {t('calibrations.documents-btn-download')}
        </Button>
      </div>
    </div>
  );
}

function RelatedTab({ cal }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <section>
        <h3 className="ct-eyebrow mb-2">{t('calibrations.related-intake-section')}</h3>
        {cal.intakeProtocolNumber ? (
          <div className="ct-panel p-4 flex items-center gap-3">
            <RefLink type="intake" id={cal.intakeProtocolNumber} />
            <span className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
              {cal.instrumentName || ''}
            </span>
          </div>
        ) : (
          <EmptyState icon={LinkIcon} title={t('calibrations.related-no-intake-title')} size="sm" description={t('calibrations.related-no-intake-desc')} />
        )}
      </section>

      <section>
        <h3 className="ct-eyebrow mb-2">{t('calibrations.related-order-section')}</h3>
        {cal.orderNumber ? (
          <div className="ct-panel p-4 flex items-center gap-3">
            <RefLink type="order" id={cal.orderNumber} />
            <span className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
              {cal.customerName || ''}
            </span>
          </div>
        ) : (
          <EmptyState icon={LinkIcon} title={t('calibrations.related-no-order-title')} size="sm" />
        )}
      </section>
    </div>
  );
}

export default function CalibrationDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cal, setCal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [methodology, setMethodology] = useState(null);
  const [etalon, setEtalon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setForbidden(false);

    api.get(`/calibrations/${id}`)
      .then(r => {
        if (cancelled) return;
        setCal(r.data);
        // Resolve linked methodology + etalon pro Přehled tab.
        if (r.data.methodology && r.data.methodology.length === 24) {
          api.get('/methodologies').then(mr => {
            if (!cancelled) setMethodology(mr.data.find(m => m.id === r.data.methodology) || null);
          }).catch(() => {});
        }
        if (r.data.etalon && r.data.etalon.length === 24) {
          api.get('/etalons').then(er => {
            if (!cancelled) setEtalon(er.data.find(e => e.id === r.data.etalon) || null);
          }).catch(() => {});
        }
      })
      .catch(err => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404) setNotFound(true);
        else if (status === 403) setForbidden(true);
        else setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [id]);

  const downloadPdf = async () => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${baseUrl}/api/calibrations/${id}/certificate.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${cal?.certificateNumber || 'kalibracni-list'}.pdf`;
    a.click();
  };

  const downloadDcc = async () => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${baseUrl}/api/calibrations/${id}/dcc-xml`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${cal?.certificateNumber || 'dcc'}.xml`;
    a.click();
  };

  const remove = async () => {
    // Mazání kalibrace je nevratné a povolené jen adminovi (backend to vynucuje).
    if (!window.confirm(t('calibrations.detail-delete-confirm'))) return;
    setDeleting(true);
    try {
      await api.delete(`/calibrations/${id}`);
      toast.success(t('calibrations.detail-delete-toast'));
      navigate('/calibrations');
    } catch (e) {
      toastApiError(e);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState message={t('calibrations.detail-loading')} />;
  if (forbidden) {
    return (
      <EmptyState
        icon={FlaskConical}
        title={t('calibrations.detail-forbidden-title')}
        description={t('calibrations.detail-forbidden-desc')}
        action={<Button onClick={() => navigate('/calibrations')} className="ct-btn-secondary">{t('calibrations.detail-back-list')}</Button>}
      />
    );
  }
  if (notFound || !cal) {
    return (
      <EmptyState
        icon={FlaskConical}
        title={t('calibrations.detail-not-found-title')}
        description={t('calibrations.detail-not-found-desc')}
        action={<Button onClick={() => navigate('/calibrations')} className="ct-btn-secondary">{t('calibrations.detail-back-list')}</Button>}
      />
    );
  }

  return (
    <div data-testid="calibration-detail-page" className="space-y-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <button
            type="button"
            onClick={() => navigate('/calibrations')}
            className="flex items-center gap-1 text-sm transition-colors hover:opacity-80"
            style={{ color: 'var(--ct-text-secondary)' }}
            title={t('calibrations.detail-back-list-title')}
          >
            <ArrowLeft className="w-4 h-4" />
            {t('calibrations.detail-back-list-label')}
          </button>
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="ct-h1 font-mono truncate" style={{ color: 'var(--ct-accent)' }}>
              {cal.certificateNumber || cal.instrumentName || t('calibrations.detail-h1-fallback')}
            </h1>
            <span className="text-sm truncate" style={{ color: 'var(--ct-text-secondary)' }}>
              {cal.certificateNumber ? (cal.instrumentName || '') : (cal.customerName || '')}
            </span>
          </div>
          <StatusBadge status={cal.status} className="flex-shrink-0" />
          {cal.result && <StatusBadge status={cal.result} className="flex-shrink-0" />}
        </div>
        <div className="flex items-center gap-2">
          {['pending_intake', 'in_progress'].includes(cal.status) && (() => {
            const missing = [];
            if (!cal.instrumentName) missing.push(t('calibrations.detail-missing-instr-name'));
            if (!cal.instrumentType) missing.push(t('calibrations.detail-missing-instr-type'));
            if (!cal.methodology) missing.push(t('calibrations.detail-missing-methodology'));
            if (!cal.etalon) missing.push(t('calibrations.detail-missing-etalon'));
            const disabled = missing.length > 0;
            const label = cal.status === 'in_progress' ? t('calibrations.detail-btn-run-continue') : t('calibrations.detail-btn-run-start');
            const tooltip = disabled
              ? t('calibrations.detail-btn-run-disabled-tooltip', { fields: missing.join(', ') })
              : t('calibrations.detail-btn-run-tooltip');
            if (disabled) {
              return (
                <span
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded font-medium cursor-not-allowed opacity-50"
                  style={{ background: 'var(--ct-accent)', color: '#fff' }}
                  title={tooltip}
                >
                  <Play className="w-3.5 h-3.5" />
                  {label}
                </span>
              );
            }
            return (
              <Link
                to={`/calibrations/${id}/run`}
                className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded transition-opacity hover:opacity-90 font-medium"
                style={{ background: 'var(--ct-accent)', color: '#fff' }}
                title={tooltip}
              >
                <Play className="w-3.5 h-3.5" />
                {label}
              </Link>
            );
          })()}
          <Link
            to={`/calibrations?edit=${id}`}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border transition-colors"
            style={{
              background: 'var(--ct-surface)',
              borderColor: 'var(--ct-border)',
              color: 'var(--ct-text)',
            }}
            title={t('calibrations.detail-btn-edit-title')}
          >
            <Edit2 className="w-3.5 h-3.5" />
            {t('calibrations.detail-btn-edit')}
          </Link>
          {user?.isAdmin && (
            <button
              type="button"
              data-testid="delete-calibration-btn"
              onClick={remove}
              disabled={deleting}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border transition-colors disabled:opacity-50"
              style={{
                background: 'var(--ct-surface)',
                borderColor: 'var(--ct-border)',
                color: 'var(--ct-error)',
              }}
              title={t('calibrations.detail-btn-delete-title')}
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {t('calibrations.detail-btn-delete')}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b overflow-x-auto" style={{ borderColor: 'var(--ct-border)' }}>
        {TABS_BASE.map(t => (
          <TabButton
            key={t.id}
            tab={t}
            active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
          />
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab
            cal={cal}
            methodology={methodology}
            etalon={etalon}
            onRefresh={() => api.get(`/calibrations/${id}`).then(r => setCal(r.data)).catch(() => {})}
          />
        )}
        {activeTab === 'history' && <HistoryTab cal={cal} />}
        {activeTab === 'documents' && <DocumentsTab cal={cal} onDownloadPdf={downloadPdf} onDownloadDcc={downloadDcc} />}
        {activeTab === 'related' && <RelatedTab cal={cal} />}
      </div>
    </div>
  );
}
